import React, { memo, useCallback, useEffect, useState } from 'react';
import Map from '@/components/Map';
import Form from '@/shared/Form';
import { useForm, Controller } from 'react-hook-form';
import { inputOnlyNumbers } from '@/utils/formValidation';
import Div from '@/shared/Div';
import Paragraph from '@/shared/Paragraph';
import Button from '@/shared/Button';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { updateServiceLocation } from '@/actions/updateServiceLocation';
import useToast from '@/hooks/useToast';
import Modal from '@/shared/crisp/Modal';
import useModalToggle from '@/hooks/useModalToggle';
import AddressConfirmation from './AddressConfirmation';
import Input from '@/shared/Input';

interface IInputFieldBigField {
  name: string;
  id: string;
  disabled?: boolean;
  label: string;
  type: string;
  value: string;
  rules: any;
}

type FormValues = {
  flatNo: any;
  building: any;
  subArea: any;
  area: any;
  landmark: string;
  pincode: string;
  city: any;
  state: any;
};

const ADDRESS_FORM_FIELDS: IInputFieldBigField[] = [
  {
    name: 'flatNo',
    id: 'flatNo',
    disabled: false,
    label: 'Flat/House no*',
    type: 'text',
    value: '',
    rules: {
      maxLength: 10,
      required: true,
    },
  },
  {
    name: 'building',
    id: 'building',
    label: 'Building/Street*',
    type: 'text',
    value: '',
    rules: {
      minLength: 3,
      maxLength: 35,
      required: true,
    },
  },

  {
    name: 'landmark',
    id: 'landmark',
    label: 'Landmark*',
    type: 'text',
    value: '',
    rules: {
      minLength: 3,
      maxLength: 35,
      required: false,
    },
  },

  {
    name: 'subArea',
    id: 'subArea',
    label: 'SubArea/Locality*',
    type: 'text',
    value: '',
    rules: {
      minLength: 3,
      maxLength: 35,
      required: true,
    },
  },

  {
    name: 'area',
    id: 'area',
    label: 'Area*',
    type: 'text',
    value: '',
    rules: {
      minLength: 3,
      maxLength: 35,
      required: true,
    },
  },
];

interface AddressFormProps {
  pincode?: string;
  location?: any;
  closeModal: Function;
  addressDetails: any;
  latLong?: { lat: number; lng: number } | null;
}

const ServiceAddressForm = (props: AddressFormProps) => {
  const { triggerDefault } = useToast();
  const { pincode, closeModal, addressDetails, latLong, location } = props || {};
  const [userFilledAddress, setUserFilledAddress] = useState({});

  const { address1, address2, address3, address4, state, city, landmark } = addressDetails;
  const { open, openModal, closeModal: closeAddressConfirmModal } = useModalToggle(false);

  let {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid, isSubmitted },
  } = useForm({
    defaultValues: {
      flatNo: '',
      building: address2 ?? '',
      subArea: address3 ?? '',
      area: address4 ?? '',
      landmark: landmark ?? '',
      pincode: pincode ?? '',
      city: city ?? '',
      state: state ?? '',
    },
  });
  const formValues = watch();
  const handleBackClick = () => {
    closeModal();
  };

  const fieldsToValidate: Array<keyof FormValues> = ['area', 'subArea', 'landmark', 'building'];
  fieldsToValidate.forEach((field) => {
    if (formValues[field].length < 3 && isSubmitted) {
      isValid = false;
      errors[field] = {
        type: 'minimumLengthValidation',
      };
    } else {
      errors[field] = {
        type: '',
      };
    }
  });

  const fetch = (...args: any) => updateServiceLocation.apply(null, args);
  const serviceLocation = useTriggerRequest({ fetch });

  useEffect(() => {
    if (serviceLocation?.isSuccess) {
      triggerDefault({
        message: serviceLocation?.data?.data,
        durationInms: 7000,
      });
    }
    if (serviceLocation?.isError) {
      triggerDefault({
        message: serviceLocation?.error?.error,
        durationInms: 5000,
      });
    }
  }, [serviceLocation?.isError, serviceLocation?.isSuccess]);

  const renderFormField = useCallback(
    (item: any, index: number) => {
      return (
        <Controller
          key={item.name}
          name={item.name}
          control={control}
          rules={item.rules}
          render={({ field }) => (
            <Input
              id={item.name}
              placeholder={item.label}
              {...field}
              errors={errors}
              onKeyPress={item.name === 'pincode' ? inputOnlyNumbers : undefined}
              maxLength={item.rules.maxLength}
            />
          )}
        />
      );
    },
    [control, errors, formValues?.pincode?.length, isSubmitted],
  );

  const formSubmitHandler = useCallback(async (formData: any) => {
    setUserFilledAddress(formData);
    openModal();
  }, []);

  return (
    <Div className="flex flex-col gap-s5 px-s6 pt-s6 md:w-[520px] overflow-y-auto">
      <Paragraph className="text-h-3">Enter Location Details</Paragraph>
      <Paragraph className="text h-4 font-bold capitalize">{`${city}, ${state} ${pincode}`}</Paragraph>
      <Form className="flex flex-wrap flex-col  gap-s4" onSubmit={handleSubmit(formSubmitHandler)}>
        <Map list={ADDRESS_FORM_FIELDS} data-testid="service-address" render={renderFormField} />
        <Div className="py-s6 flex gap-s3 ">
          <Button theme="secondary" className="flex-grow" onClick={handleBackClick}>
            Go Back
          </Button>
          <Button
            className="flex-grow"
            type="submit"
            // disabled={!isValid}
            isLoading={serviceLocation?.isLoading}
          >
            Continue
          </Button>
        </Div>
      </Form>

      <Modal open={open} onClose={closeAddressConfirmModal}>
        <AddressConfirmation
          closeModal={closeAddressConfirmModal}
          address={userFilledAddress}
          latLong={latLong}
          preciseLocation={location?.preciseLocation}
        />
      </Modal>
    </Div>
  );
};

export default memo(ServiceAddressForm);
